import { FOLLOWERS_OR_FOLLOWING_PAGE_SIZE } from "@/constants/user"
import { ResetPasswordRequestBody } from "@/types/auth"
import type { CreateChatActionPill } from "@/types/chat"
import type { LocationSeedSource } from "@/types/home-location-seed"
import { LocationSummary, Place } from "@/types/place"
import {
  CurrentUser,
  CurrentUserProfile,
  FollowerInfo,
  FollowStatus,
  GetCurrenciesResponse,
  LoginAuthProvider,
  PublicUser,
  PublicUserProfile,
  UpdateUserPasswordResults,
  UpdateUserProfileResults,
  UpdateUserProperties,
  UpdateUserPropertiesResults,
  UserPasswordUpdate,
  UserPermission,
  UserProfileUpdates,
  UserRef,
} from "@/types/user"

import { BaseApi, BaseApiRequestConfig } from "./base-api"
import { DEFAULT_PLACES_PER_PAGE, PagingMeta } from "./paging"

export type TripType =
  | "family"
  | "business"
  | "solo"
  | "couple"
  | "extended_family"
  | "friends"
  | "other"

export class UserApi extends BaseApi {
  async fetchUser(): Promise<CurrentUser> {
    return await this.get<CurrentUser>(`/api/users/current`)
  }

  /** precise: device fix or home only. fallback: assistant Home seed chain with source. */
  async fetchUserLocation(
    options: { precise?: boolean; fallback?: boolean } = {}
  ) {
    const params: { precise?: boolean; fallback?: boolean } = {}
    if (options.precise) params.precise = true
    if (options.fallback) params.fallback = true
    return await this.get<{
      location: LocationSummary | undefined
      source?: LocationSeedSource
      approximate_from?: string
    }>(`/api/users/current/location`, {
      params: Object.keys(params).length > 0 ? params : undefined,
    })
  }

  async fetchPublicUser(handle: string): Promise<PublicUser> {
    return await this.get<PublicUser>(`/api/users/${handle}`)
  }

  async fetchPublicUsersByRef(userRefs: UserRef[]) {
    const refs = userRefs.join(",")
    const url = `/api/users?refs=${refs}&include_profile=1`
    return (await this.get<{ users: PublicUser[] }>(url)).users
  }

  async fetchPublicUserByRef(userRef: UserRef) {
    return (
      await this.get<{ users: PublicUser[] }>(`/api/users?refs=${userRef}`)
    ).users[0]
  }

  async fetchUserProfile(): Promise<CurrentUserProfile> {
    return await this.get<CurrentUserProfile>(`/api/user_profile/current`)
  }

  async fetchCurrencies(): Promise<GetCurrenciesResponse> {
    return await this.get<GetCurrenciesResponse>(
      `/api/users/currencies?all=true`
    )
  }

  async fetchPublicUserProfile(handle: string): Promise<PublicUserProfile> {
    return await this.get<PublicUserProfile>(`/api/user_profile/${handle}`)
  }

  async fetchUserPlaces({
    page = 1,
    perPage = DEFAULT_PLACES_PER_PAGE,
    userRef,
  }: {
    page?: number
    perPage?: number
    userRef: UserRef
  }) {
    return await this.get<{ data: Place[]; meta: PagingMeta }>(
      `/api/users/${userRef}/inspiration_places?page=${page}&per_page=${perPage}`
    )
  }

  async fetchUserPlacesCount(userRef: UserRef) {
    return (
      await this.get<{ count: number }>(
        `/api/users/${userRef}/inspiration_places/count`
      )
    ).count
  }

  async updateUserProfile(
    userProfileDTO: UserProfileUpdates,
    customConfig?: BaseApiRequestConfig
  ): Promise<UpdateUserProfileResults> {
    return await this.put(
      `/api/user_profile`,
      {
        user_profile: userProfileDTO,
      },
      customConfig
    )
  }

  async resetTravelAssistantSetup(): Promise<UpdateUserProfileResults> {
    return await this.post(`/api/user_profile/reset_travel_assistant_setup`, {})
  }

  async submitIntroInterviewMemory(payload: {
    usual_trip_type?: TripType
    usual_trip_types?: TripType[]
    family_members?: Array<{
      relationship: "kid" | "parent" | "partner" | "sibling" | "other"
      age?: number
    }>
    countries_visited_location_refs?: string[]
    favorite_city_location_refs?: string[]
    future_location_refs?: string[]
    important_locations?: Array<{ label: string; address: string }>
    travel_budget?: string
    splurge_categories?: string[]
    accommodation_styles?: string[]
    loyalty_programs?: string[]
    loyalty_program_keys?: string[]
    replace_loyalty_programs?: boolean
    unlisted_loyalty_programs?: { program_name: string; program_type: string }[]
    dining_styles?: string[]
    dietary_restrictions?: string[]
    weekend_activities?: string[]
    additional_notes?: string
    complete_setup?: boolean
  }): Promise<{ enqueued: boolean; next_steps: CreateChatActionPill[] }> {
    return await this.post(`/api/user_memories/from_intro_interview`, payload)
  }

  async updateUserProperties(
    userPropertiesDTO: UpdateUserProperties
  ): Promise<UpdateUserPropertiesResults> {
    return await this.put(`/api/user_properties`, {
      user_properties: userPropertiesDTO,
    })
  }

  async updateUserPassword(
    userPasswordDTO: UserPasswordUpdate
  ): Promise<UpdateUserPasswordResults> {
    return await this.put(`/api/auth/update_password`, userPasswordDTO)
  }

  async checkHandle(handle: string): Promise<{
    available: boolean
    reserved?: boolean
    suggested?: string
  }> {
    const query = new URLSearchParams()
    query.append("handle", handle)

    return await this.get(`/api/users/check_handle?${query.toString()}`)
  }

  async uploadAvatarImage(
    formData: FormData
  ): Promise<{ url: string; error?: string }> {
    return await this.post(`/api/user_profile/upload_avatar_img`, formData, {
      headers: {
        "content-type": "multipart/form-data",
      },
    })
  }

  // async uploadFile(
  // 	formData: FormData
  // ): Promise<{ url: string; error?: string }> {
  // 	return await this.post(`/api/user/upload_file`, formData, {
  // 		headers: {
  // 			"content-type": "multipart/form-data",
  // 		},
  // 	})
  // }

  async resetPassword(reset_password_token: string, new_password: string) {
    return await this.post<{ user: CurrentUser }, ResetPasswordRequestBody>(
      `/api/auth/reset_password`,
      {
        reset_password_token,
        new_password,
      },
      // no need to refresh session token
      { skipAuthHandling: true }
    )
  }

  async generateVerifyEmail(
    mode: "force" | "check" = "force"
  ): Promise<{ status: string; message: string }> {
    return await this.post(`/api/auth/generate_email_verification`, {
      mode,
      numeric_email_validation_code: true,
    })
  }

  async verifyEmail(code: string): Promise<{
    status: "ok" | "redeemed" | "expired" | "error"
    message: string
    user: PublicUser
    email_changed?: boolean
  }> {
    return await this.post(`/api/auth/verify/email`, { code })
  }

  async initiateEmailChange(
    newEmail: string,
    password: string
  ): Promise<{ status: string; message: string }> {
    return await this.post(`/api/auth/initiate_email_change`, {
      new_email: newEmail,
      password: password,
    })
  }

  async getProvidersByEmail(email: string): Promise<{
    value: LoginAuthProvider[]
  }> {
    return await this.post(`/api/auth/check_providers`, { email })
  }

  async getPermissions(): Promise<UserPermission[]> {
    return (
      await this.get<{ permissions: UserPermission[] }>(`api/users/permissions`)
    ).permissions
  }

  async unsubscribe_email(email: string | string[], key: string | string[]) {
    return await this.post<void>(`api/unsigned_in_users/unsubscribe_email`, {
      email: email,
      key: key,
    })
  }

  async clearAccount() {
    return await this.post<void>(`api/users/clear_account`, {})
  }

  async followUser(userRef: UserRef): Promise<FollowStatus> {
    const res = await this.post<{ follow_status: string }>(
      `/api/follows/follow?user_ref=${userRef}`
    )
    return res.follow_status as FollowStatus
  }

  async unfollowUser(userRef: UserRef): Promise<void> {
    return await this.delete(`/api/follows/unfollow?user_ref=${userRef}`)
  }

  async isFollowing(userRef: UserRef): Promise<FollowStatus> {
    const res = await this.get<{ follow_status: string }>(
      `/api/follows/is_following?user_ref=${userRef}`
    )
    return res.follow_status as FollowStatus
  }

  async getFollowers(
    userRef?: UserRef,
    page = 1,
    perPage: number = FOLLOWERS_OR_FOLLOWING_PAGE_SIZE
  ) {
    const endpoint = userRef
      ? `/api/follows/followers?user_ref=${userRef}&page=${page}&per_page=${perPage}`
      : `/api/follows/followers?page=${page}&per_page=${perPage}`
    return await this.get<{
      follower_infos: FollowerInfo[]
      meta: PagingMeta
    }>(endpoint)
  }

  async getFollowing(
    userRef?: UserRef,
    page = 1,
    perPage: number = FOLLOWERS_OR_FOLLOWING_PAGE_SIZE
  ) {
    const endpoint = userRef
      ? `/api/follows/followees?user_ref=${userRef}&page=${page}&per_page=${perPage}`
      : `/api/follows/followees?page=${page}&limit=${perPage}`
    return await this.get<{
      followees: PublicUser[]
      meta: PagingMeta
    }>(endpoint)
  }

  async authorizeStream(streamId: string) {
    await this.head(
      `/api/users/authorize_stream/${encodeURIComponent(streamId)}`
    )
  }
}
